<div class="page">
  <div class="header">
    <h1 class="title">Payroll Deductions</h1>
    <div class="subtitle">Approve compatible deduction codes and map benefits to payroll deduction for this employer.</div>
  </div>

  @if (!isInitialCompanyCheckComplete) {
    <section class="page-loading">
      <mat-spinner [diameter]="32"></mat-spinner>
      <span>Loading</span>
    </section>
  }

  @if (isInitialCompanyCheckComplete && !hasCompany) {
    <section class="setup-cta">
      <div class="setup-cta-content">
        <mat-icon>business</mat-icon>
        <div class="setup-cta-copy">
          <h2>Create your first company</h2>
          <p>To manage payroll deductions, create a company first in Company &gt; Company.</p>
        </div>
      </div>
      <button mat-raised-button color="primary" type="button" (click)="onGoToCompanyTab()">
        Go to Company
      </button>
    </section>
  }

  @if (isInitialCompanyCheckComplete && hasCompany) {
    @if (isPayrollDeductionFeatureEnabled === false && !isLoading && !setupLoadError) {
      <section class="setup-cta">
        <div class="setup-cta-content">
          <mat-icon>toggle_off</mat-icon>
          <div class="setup-cta-copy">
            <h2>Payroll deductions are not enabled</h2>
            <p>Turn on the payroll deduction feature flag before configuring deduction codes or product mappings.</p>
          </div>
        </div>
      </section>
    }
    @if (setupLoadError && !isLoading) {
      <section class="sync-warning" role="alert">
        <mat-icon>warning_amber</mat-icon>
        <div>{{ setupLoadError }}</div>
        <button mat-stroked-button type="button" (click)="retrySetupLoad()">Retry</button>
      </section>
    }
    @if (isLoading) {
      <div class="loading">Loading payroll deduction setup...</div>
    }
    @if (!isLoading && isPayrollDeductionFeatureEnabled && !setupLoadError) {
      <mat-button-toggle-group
        class="top-tabs"
        [value]="activeTopTab"
        (change)="setActiveTopTab($event.value)"
        aria-label="Payroll deductions view"
        >
        <mat-button-toggle value="setup">
          <mat-icon>settings</mat-icon>
          Setup
        </mat-button-toggle>
        <mat-button-toggle value="history">
          <mat-icon>history</mat-icon>
          History
        </mat-button-toggle>
      </mat-button-toggle-group>
      @if (activeTopTab === 'setup') {
        @if (payrollDeductionSetupUnsupportedReason) {
          <section class="setup-cta">
            <div class="setup-cta-content">
              <mat-icon>block</mat-icon>
              <div class="setup-cta-copy">
                <h2>Payroll deduction setup unavailable</h2>
                <p>{{ payrollDeductionSetupUnsupportedReason }}</p>
              </div>
            </div>
          </section>
        }
        @if (!payrollDeductionSetupUnsupportedReason && !providerConfigured) {
          <section class="setup-cta">
            <div class="setup-cta-content">
              <mat-icon>link_off</mat-icon>
              <div class="setup-cta-copy">
                <h2>{{ connectionRequiredTitle }}</h2>
                <p>{{ connectionRequiredCopy }}</p>
              </div>
            </div>
            <button mat-raised-button color="primary" type="button" (click)="onGoToConnectionsTab()">
              Go to Connections
            </button>
          </section>
        }
        @if (!payrollDeductionSetupUnsupportedReason && providerConfigured) {
          @if (isDeductionSetupRefreshing) {
            <section class="sync-warning sync-warning--refreshing">
              <mat-spinner [diameter]="20"></mat-spinner>
              <div>
                <strong>Refreshing provider deduction codes.</strong>
                Saved approvals and mappings are shown while {{ activePayrollProviderName }} codes finish loading.
              </div>
            </section>
          }
          @if (liveRefreshError) {
            <section class="sync-warning" role="alert">
              <mat-icon>warning_amber</mat-icon>
              <div>{{ liveRefreshError }} Saved approvals and mappings are still shown.</div>
              <button mat-stroked-button type="button" [disabled]="isDeductionSetupRefreshing || isSaving"
                (click)="retryLiveRefresh()">Retry refresh</button>
            </section>
          }
          @if (providerSyncError) {
            <section class="sync-warning">
              <mat-icon>warning_amber</mat-icon>
              <div>
                <strong>{{ providerSyncIssueTitle }}</strong>
                {{ providerSyncError }} {{ providerSavedCodesWarning }}
              </div>
            </section>
          }
          <div class="grid">
            <div class="workflow-column">
              <div class="summary">
                <div class="step">
                  <span class="step-index">1</span>
                  <span class="step-text">Approve the deduction codes that are safe to use for scheduled deductions.</span>
                </div>
                <div class="step">
                  <span class="step-index">2</span>
                  <span class="step-text">Map each benefit product to an approved default code or plan/tier overrides.</span>
                </div>
                <div class="summary-actions">
                  <button
                    mat-stroked-button
                    type="button"
                    [disabled]="isDeductionSetupEditDisabled || !hasUnsavedChanges"
                    (click)="resetChanges()"
                    >
                    Reset
                  </button>
                  <button mat-raised-button color="primary" [disabled]="isDeductionSetupEditDisabled || !hasUnsavedChanges" (click)="save()">
                    Save Payroll Deductions
                  </button>
                </div>
              </div>
              <section class="panel codes-panel" [class.panel--collapsed]="!isCodesPanelExpanded">
                <div class="panel-header panel-header--collapsible">
                  <div class="panel-heading-copy">
                    <h2>Approved Deduction Codes</h2>
                    <div class="panel-subtitle">Only approved codes can be selected in product mappings.</div>
                  </div>
                  <div class="panel-header-actions">
                    @if (!isCodesPanelExpanded) {
                      <span class="badge panel-count-badge">
                        {{ selectedAllowedCodesCount }} approved
                      </span>
                    }
                    <button mat-icon-button type="button" class="panel-toggle" (click)="toggleCodesPanel()">
                      <mat-icon>{{ isCodesPanelExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                    </button>
                  </div>
                </div>
                @if (isCodesPanelExpanded) {
                  @if (isUkgReadySetup) {
                    <div class="guidance-card">
                      <p class="guidance-card__warning">
                        Before approving a code, confirm in UKG Ready that the deduction code's Employee section has both
                        <strong>Default Amount</strong> and <strong>Frequency</strong> configured. If you approve a deduction
                        code without those defaults, products may be purchased without the corresponding payroll deductions
                        pushing into UKG Ready, so employees may effectively receive products for free. If this occurs, it is
                        your legal and financial responsibility, not Wallit's.
                      </p>
                    </div>
                  } @else {
                    <div class="guidance-card">
                      <p class="guidance-card__warning">
                        Approving codes saves Wallit setup and product mappings only. Confirm the selected legal entity
                        deduction codes are correct in {{ activePayrollProviderName }} before mapping products; this does
                        not enroll employees, create deductions, run purchases, or update {{ activePayrollProviderName }}.
                      </p>
                    </div>
                  }
                  @if (availableDeductionCodes.length === 0) {
                    <div class="empty">
                      No deduction codes are available yet for this employer.
                    </div>
                  }
                  @if (availableDeductionCodes.length > 0) {
                    <div class="code-list">
                      <div class="code-row header">
                        <div class="col-approve">Approve</div>
                        <div class="col-code">Code</div>
                        <div class="col-description">Description</div>
                        <div class="col-status">Status</div>
                      </div>
                      @for (code of availableDeductionCodes; track trackDeductionCode($index, code)) {
                        <div class="code-row">
                          <div class="col-approve">
                            <mat-checkbox
                              [checked]="isDeductionCodeApproved(code)"
                              [disabled]="isAllowedCodeToggleDisabled(code)"
                              (change)="onAllowedCodeToggle(code, $event)"
                              >
                            </mat-checkbox>
                          </div>
                          <div class="col-code">{{ code.code }}</div>
                          <div class="col-description">
                            <div>{{ code.description || noDeductionDescriptionText }}</div>
                            @if (getMappedProductCountForCode(code) > 0) {
                              <div class="mapped-helper">
                                Used by {{ getMappedProductCountForCode(code) }} product@if (getMappedProductCountForCode(code) !== 1) {
                                <span>s</span>
                              }
                            </div>
                          }
                        </div>
                        <div class="col-status">
                          <span class="status-chip" [class.status-chip--saved]="isDeductionCodeSavedOnly(code)">
                            {{ getDeductionCodeStatusLabel(code) }}
                          </span>
                        </div>
                      </div>
                    }
                  </div>
                }
              }
            </section>
          </div>
          <section class="panel mappings-panel" [class.panel--collapsed]="!isMappingsPanelExpanded">
            <div class="panel-header panel-header--collapsible">
              <div class="panel-heading-copy">
                <h2>Product Mappings</h2>
                <div class="panel-subtitle">Enable products for payroll deduction mapping, then choose default codes or optional plan/tier overrides.</div>
              </div>
              <div class="panel-header-actions">
                <span class="badge panel-count-badge">
                  {{ mappedProductsCount }} mapped
                </span>
                <button mat-icon-button type="button" class="panel-toggle" (click)="toggleMappingsPanel()">
                  <mat-icon>{{ isMappingsPanelExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                </button>
              </div>
            </div>
            @if (isMappingsPanelExpanded) {
              <div class="mapping-filter-bar">
                <mat-button-toggle-group
                  [value]="productVisibilityFilter"
                  (change)="onProductVisibilityFilterChanged($event.value)"
                  [disabled]="isSaving"
                  aria-label="Product visibility"
                  >
                  <mat-button-toggle value="selectedByTeam">
                    Products available to users
                    <span class="filter-count">{{ selectedByTeamProductsCount }}</span>
                  </mat-button-toggle>
                  <mat-button-toggle value="all">
                    All products
                    <span class="filter-count">{{ payrollDeductionAllowedProductsCount }}</span>
                  </mat-button-toggle>
                </mat-button-toggle-group>
              </div>
              @if (approvedDeductionCodes.length === 0) {
                <div class="empty">
                  Approve at least one deduction code before mapping products.
                  <ul aria-label="Products awaiting deduction code approval">
                    @for (product of getVisibleBenefitProducts(); track product.id) {
                      <li>{{ product.vendorName }} — {{ product.productName }}</li>
                    }
                  </ul>
                </div>
              }
              @if (approvedDeductionCodes.length > 0) {
                @if (payrollDeductionAllowedProductsCount === 0) {
                  <div class="empty">
                    No products are allowed for payroll deduction for this company.
                  </div>
                }
                @if (payrollDeductionAllowedProductsCount > 0 && visibleBenefitProductsCount === 0) {
                  <div class="empty">
                    No products have been selected by a team yet. Switch to All products to configure available products.
                  </div>
                }
                @for (category of categories; track trackCategory($index, category)) {
                  @if (getBenefitSections(category.id).length > 0) {
                    <div class="category">
                      <button
                        type="button"
                        class="category-toggle"
                        (click)="toggleMappingCategory(category.id)"
                        >
                        <span>{{ category.title }}</span>
                        <mat-icon>{{ isMappingCategoryExpanded(category.id) ? 'expand_less' : 'expand_more' }}</mat-icon>
                      </button>
                      @if (isMappingCategoryExpanded(category.id)) {
                        @for (section of getBenefitSections(category.id); track trackSection($index, section)) {
                          <div class="section">
                            <button
                              type="button"
                              class="section-toggle"
                              (click)="toggleMappingSection(category.id, section.id)"
                              >
                              <span class="section-title">{{ section.title }}</span>
                              <span class="section-toggle-meta">
                                @if (getMappedProductCountForSection(section) > 0) {
                                  <span
                                    class="mapping-count-badge"
                                    >
                                    {{ getMappedProductCountForSection(section) }} mapped
                                  </span>
                                }
                                <mat-icon>{{ isMappingSectionExpanded(category.id, section.id) ? 'expand_less' : 'expand_more' }}</mat-icon>
                              </span>
                            </button>
                            @if (isMappingSectionExpanded(category.id, section.id)) {
                              @for (product of section.products; track trackProduct($index, product)) {
                                <div
                                  class="product-mapping"
                                  [class.product-mapping--disabled]="!isProductPayrollDeductionMappingEnabled(product)"
                                  >
                                  <div class="product-row">
                                    <div class="product-enable">
                                      <mat-checkbox
                                        aria-label="Enable payroll deduction mapping"
                                        [checked]="isProductPayrollDeductionMappingEnabled(product)"
                                        [disabled]="isSaving"
                                        (change)="onProductMappingEnabledToggle(product, $event)"
                                        >
                                      </mat-checkbox>
                                    </div>
                                    <div class="product-copy">
                                      <div class="product-name">{{ product.vendorName || product.productName }}</div>
                                      <div class="product-description">{{ product.productName }}</div>
                                      <span class="mapping-state-chip">{{ getProductMappingStateLabel(product) }}</span>
                                    </div>
                                    <div class="mapping-controls">
                                      <mat-form-field appearance="outline" class="mapping-field">
                                        <mat-label>{{ hasProductPlanOptions(product) ? 'Product-level deduction code' : 'Deduction code' }}</mat-label>
                                        <mat-select
                                          [value]="getProductDeductionCode(product.id)"
                                          [disabled]="isProductMappingControlDisabled(product)"
                                          (selectionChange)="onProductDeductionCodeChanged(product, $event.value)"
                                          >
                                          <mat-option value="">{{ hasProductPlanOptions(product) ? 'No product-level default' : 'No deduction code' }}</mat-option>
                                          @for (code of approvedDeductionCodes; track trackDeductionCode($index, code)) {
                                            <mat-option [value]="code.code">
                                              {{ code.code }}@if (code.description) {
                                              <span> - {{ code.description }}</span>
                                            }
                                          </mat-option>
                                        }
                                      </mat-select>
                                    </mat-form-field>
                                    @if (hasProductPlanOptions(product)) {
                                      <div class="advanced-config">
                                        <button
                                          mat-stroked-button
                                          type="button"
                                          class="advanced-toggle"
                                          [disabled]="isProductMappingControlDisabled(product)"
                                          (click)="toggleAdvancedEditor(product)"
                                          >
                                          <mat-icon>tune</mat-icon>
                                          {{ hasProductPlanTierCodes(product) ? 'Edit plan/tier codes' : 'Configure plan/tier codes' }}
                                        </button>
                                        @if (getPlanTierCodeSummary(product)) {
                                          <div
                                            class="advanced-collapsed-summary"
                                            [class.advanced-collapsed-summary--warning]="getAdvancedMissingCombinationCount(product) > 0"
                                            >
                                            <mat-icon>{{ getAdvancedMissingCombinationCount(product) > 0 ? 'warning' : 'check_circle' }}</mat-icon>
                                            <span>{{ getPlanTierCodeSummary(product) }}</span>
                                          </div>
                                        }
                                      </div>
                                    }
                                  </div>
                                </div>
                                @if (isProductPayrollDeductionMappingEnabled(product)) {
                                  <div class="product-payment-note">
                                    <mat-icon>payments</mat-icon>
                                    <span>
                                      Products mapped here are paid by <strong>Payroll Deduction only</strong> in the customer app. A product-level code applies to every purchase unless a matching plan/tier override is set.
                                    </span>
                                  </div>
                                }
                                @if (isProductPayrollDeductionMappingEnabled(product) && hasProductPlanOptions(product) && isAdvancedEditorOpen(product)) {
                                  <div
                                    class="advanced-editor"
                                    >
                                    <div class="advanced-editor-header">
                                      <div>
                                        <div class="advanced-editor-title">Plan/tier codes</div>
                                        @if (getAdvancedMissingCombinationCount(product) > 0) {
                                          <div class="advanced-editor-warning">
                                            {{ getAdvancedMissingCombinationCount(product) }} missing
                                          </div>
                                        }
                                      </div>
                                      <div class="advanced-bulk-controls">
                                        <mat-form-field appearance="outline" class="bulk-field">
                                          <mat-label>Apply one code</mat-label>
                                          <mat-select
                                            [value]="getBulkCode(product.id, 'all')"
                                            [disabled]="isProductMappingControlDisabled(product)"
                                            (selectionChange)="onBulkCodeChanged(product.id, 'all', $event.value)"
                                            >
                                            <mat-option value="">Choose code</mat-option>
                                            @for (code of approvedDeductionCodes; track trackDeductionCode($index, code)) {
                                              <mat-option [value]="code.code">
                                                {{ code.code }}@if (code.description) {
                                                <span> - {{ code.description }}</span>
                                              }
                                            </mat-option>
                                          }
                                        </mat-select>
                                      </mat-form-field>
                                      <button
                                        mat-stroked-button
                                        type="button"
                                        [disabled]="isProductMappingControlDisabled(product) || !getBulkCode(product.id, 'all')"
                                        (click)="applyBulkCodeToAll(product)"
                                        >
                                        <mat-icon>done_all</mat-icon>
                                        Apply all
                                      </button>
                                      <button
                                        mat-stroked-button
                                        type="button"
                                        [disabled]="isProductMappingControlDisabled(product) || !hasProductPlanTierCodes(product)"
                                        (click)="clearProductPlanTierCodes(product)"
                                        >
                                        <mat-icon>clear_all</mat-icon>
                                        Clear all
                                      </button>
                                    </div>
                                  </div>
                                  <div class="matrix-wrap">
                                    <table class="mapping-matrix">
                                      <thead>
                                        <tr>
                                          <th>Plan</th>
                                          <th>Apply plan</th>
                                          @for (tier of getProductTierColumns(product); track tier) {
                                            <th>
                                              {{ getTierDisplayLabel(tier) }}
                                            </th>
                                          }
                                        </tr>
                                      </thead>
                                      <tbody>
                                        @for (plan of getProductPlanRows(product); track plan) {
                                          <tr>
                                            <td class="plan-cell">{{ getPlanDisplayLabel(plan) }}</td>
                                            <td>
                                              <div class="plan-fill">
                                                <mat-form-field appearance="outline" class="matrix-field">
                                                  <mat-label>Code</mat-label>
                                                  <mat-select
                                                    [value]="getBulkCode(product.id, getPlanBulkScope(plan))"
                                                    [disabled]="isProductMappingControlDisabled(product)"
                                                    (selectionChange)="onBulkCodeChanged(product.id, getPlanBulkScope(plan), $event.value)"
                                                    >
                                                    <mat-option value="">Choose code</mat-option>
                                                    @for (code of approvedDeductionCodes; track trackDeductionCode($index, code)) {
                                                      <mat-option [value]="code.code">
                                                        {{ code.code }}@if (code.description) {
                                                        <span> - {{ code.description }}</span>
                                                      }
                                                    </mat-option>
                                                  }
                                                </mat-select>
                                              </mat-form-field>
                                              <button
                                                mat-icon-button
                                                type="button"
                                                [disabled]="isProductMappingControlDisabled(product) || !getBulkCode(product.id, getPlanBulkScope(plan))"
                                                (click)="applyBulkCodeToPlan(product, plan)"
                                                >
                                                <mat-icon>keyboard_double_arrow_right</mat-icon>
                                              </button>
                                            </div>
                                          </td>
                                          @for (tier of getProductTierColumns(product); track tier) {
                                            <td>
                                              <mat-form-field appearance="outline" class="matrix-field">
                                                <mat-label>Code</mat-label>
                                                <mat-select
                                                  [value]="getMatrixDeductionCode(product, plan, tier)"
                                                  [disabled]="isProductMappingControlDisabled(product)"
                                                  (selectionChange)="onMatrixDeductionCodeChanged(product, plan, tier, $event.value)"
                                                  >
                                                  <mat-option value="">Not set</mat-option>
                                                  @for (code of approvedDeductionCodes; track trackDeductionCode($index, code)) {
                                                    <mat-option [value]="code.code">
                                                      {{ code.code }}@if (code.description) {
                                                      <span> - {{ code.description }}</span>
                                                    }
                                                  </mat-option>
                                                }
                                              </mat-select>
                                            </mat-form-field>
                                          </td>
                                        }
                                      </tr>
                                    }
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          }
                        </div>
                      }
                    }
                  </div>
                }
              }
            </div>
          }
        }
      }
    }
  </section>
</div>
}
}
@if (activeTopTab === 'history') {
  <section class="panel history-panel">
    <div class="panel-header history-header">
      <div class="panel-heading-copy">
        <h2>Sync History</h2>
        <div class="panel-subtitle">Payroll deduction sync submissions sent to the payroll provider.</div>
      </div>
      <button
        mat-stroked-button
        type="button"
        [disabled]="importHistoryLoading || !!deductionSyncHistoryUnsupportedReason || !!deductionSyncStatusRefreshUnsupportedReason"
        [matTooltip]="deductionSyncHistoryUnsupportedReason || deductionSyncStatusRefreshUnsupportedReason"
        (click)="refreshImportHistory()"
        >
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
    </div>
    @if (importHistoryLoading) {
      <div class="history-loading">
        <mat-spinner [diameter]="28"></mat-spinner>
        <span>Loading history</span>
      </div>
    }
    @if (!importHistoryLoading && importHistoryError) {
      <div class="sync-warning">
        <mat-icon>warning_amber</mat-icon>
        <div>{{ importHistoryError }}</div>
      </div>
    }
    @if (!importHistoryLoading && !importHistoryError && importHistoryItems.length === 0) {
      <div class="empty">
        No payroll deduction syncs have been captured yet.
      </div>
    }
    @if (!importHistoryLoading && importHistoryItems.length > 0) {
      <div class="history-table">
        <div class="history-row history-row--header">
          <div>Created</div>
          <div>Member</div>
          <div>Product</div>
          <div>Action</div>
          <div>Status</div>
          <div>Amount</div>
          <div></div>
        </div>
        @for (item of importHistoryItems; track trackImportRecord($index, item)) {
          <div
            class="history-row history-row--item"
            role="button"
            tabindex="0"
            (click)="openImportDetails(item)"
            (keydown.enter)="openImportDetails(item)"
            >
            <div>{{ formatDateTime(item.createdAt) }}</div>
            <div>{{ formatImportPerson(item) }}</div>
            <div>{{ formatImportProduct(item) }}</div>
            <div>{{ formatImportAction(item.action) }}</div>
            <div>
              <span class="status-chip" [ngClass]="getImportStatusClass(item.status)">
                {{ formatImportStatus(item.status) }}
              </span>
            </div>
            <div>{{ formatMoneyCents(item.perPayAmountCents) }} / pay</div>
            <div class="history-actions">
              <button
                mat-icon-button
                type="button"
                aria-label="Refresh sync status"
                title="Refresh sync status"
                [matTooltip]="deductionSyncStatusRefreshUnsupportedReason"
                [disabled]="!canRefreshImportStatus(item) || refreshingImportStatusId === item.id"
                (click)="refreshImportStatus(item, $event)"
                >
                <mat-icon>{{ refreshingImportStatusId === item.id ? 'hourglass_top' : 'refresh' }}</mat-icon>
              </button>
            </div>
          </div>
        }
      </div>
    }
    @if (!importHistoryLoading && importHistoryTotal > 0) {
      <div class="history-pagination">
        <span>{{ importHistoryPageStart }}-{{ importHistoryPageEnd }} of {{ importHistoryTotal }}</span>
        <div>
          <button
            mat-icon-button
            type="button"
            [disabled]="!hasPreviousImportHistoryPage"
            (click)="goToPreviousImportHistoryPage()"
            >
            <mat-icon>chevron_left</mat-icon>
          </button>
          <button
            mat-icon-button
            type="button"
            [disabled]="!hasNextImportHistoryPage"
            (click)="goToNextImportHistoryPage()"
            >
            <mat-icon>chevron_right</mat-icon>
          </button>
        </div>
      </div>
    }
  </section>
  @if (selectedImport) {
    <section class="panel import-detail-panel">
      <div class="panel-header history-header">
        <div class="panel-heading-copy">
          <h2>Sync Details</h2>
          <div class="panel-subtitle">{{ selectedImport.id }}</div>
        </div>
        <button mat-icon-button type="button" (click)="closeImportDetails()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div class="detail-grid">
        <div>
          <span>Status</span>
          <strong>{{ formatImportStatus(selectedImport.status) }}</strong>
        </div>
        <div>
          <span>Sync job</span>
          <strong>{{ selectedImport.ukgImportJobId || 'Not returned' }}</strong>
        </div>
        <div>
          <span>Effective</span>
          <strong>{{ formatEffectiveRange(selectedImport) }}</strong>
        </div>
        <div>
          <span>Deduction</span>
          <strong>{{ selectedImport.deductionName || selectedImport.deductionCode || 'Not set' }}</strong>
        </div>
        <div>
          <span>Per pay</span>
          <strong>{{ formatMoneyCents(selectedImport.perPayAmountCents) }}</strong>
        </div>
        <div>
          <span>Annual max</span>
          <strong>{{ formatMoneyCents(selectedImport.annualAmountCents) }}</strong>
        </div>
      </div>
      @if (canReplayImport(selectedImport)) {
        <div class="detail-actions">
          <button
            mat-raised-button
            color="primary"
            type="button"
            [disabled]="replayingImportId === selectedImport.id"
            (click)="replayImport(selectedImport, $event)"
            >
            <mat-icon>replay</mat-icon>
            Replay Sync
          </button>
        </div>
      }
      @if (selectedImport.redactedCsv) {
        <div class="detail-section">
          <h3>Redacted CSV</h3>
          <pre>{{ selectedImport.redactedCsv }}</pre>
        </div>
      }
      <div class="detail-section">
        <h3>Sync Status</h3>
        <pre>{{ formatJson(selectedImport.rawStatusJson) }}</pre>
      </div>
      <div class="detail-section">
        <h3>Sync Response</h3>
        <pre>{{ formatJson(selectedImport.rawResponseJson) }}</pre>
      </div>
      @if (selectedImport.errorJson) {
        <div class="detail-section">
          <h3>Error</h3>
          <pre>{{ formatJson(selectedImport.errorJson) }}</pre>
        </div>
      }
    </section>
  }
}
}
}
</div>
